import * as React from "react";
import rehypeReact from "rehype-react";
import { LandingTitle } from "../components/landingTitle";
import { Video } from "../components/video";
import Faq from "../components/faq";
import { useSiteMetadata } from "../hooks/site-meta";
import { Affiliate } from "../components/affiliate";
import { MdTable } from "../components/table";
import { AnchorLink } from "gatsby-plugin-anchor-links";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPatreon } from "@fortawesome/free-brands-svg-icons";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { SeriesNavigation, SeriesTOC } from "../components/seriesNavigation";
import { ShowStream } from "../components/streams";

const Wrapper = ({ children }) => <>{children}</>;

const renderSection = (props) => {
  // debug(props)
  if (
    props.tagName === "section" ||
    (props.properties &&
      props.properties.className &&
      props.properties.className.includes("section"))
  ) {
    props.tagName = "section";
    props.properties.className = [
      ...(props.properties.className || []),
      "helpthis",
    ];
    return <section {...props} />;
  }
  return null;
};

const components = (metadata) => ({
  video: (props) => <Video {...props} metadata={metadata} />,
  showstream: (props) => <ShowStream {...props} metadata={metadata} />,
  embedfaq: (props) => <Faq {...props} metadata={metadata} />,
  mdtable: (props) => <MdTable {...props} metadata={metadata} />,
  element: renderSection,
  "*": null,
});

export const TemplateArticle = (props) => {
  const { post, series } = props;
  const { frontmatter: info } = post;

  let metadata = info;
  const { supplies } = useSiteMetadata();
  if (!metadata.supplies) {
    metadata.supplies = supplies;
  }

  const layout = info.class || "article";
  const linkClass = `block hover:bg-zinc-100 border-l-4 px-2 py-1 transition-colors duration-200`;

  const renderer = new rehypeReact({
    createElement: React.createElement,
    components: components(metadata),
    Fragment: Wrapper,
  }).Compiler;

  return (
    <article className={layout}>
      <LandingTitle
        title={info.title}
        subtitle={info.subtitle}
        author={info.author || null}
        featuring={info.featuring}
        dates={{
          date: info.date,
          dateTime: info.dateTime,
        }}
        modified={{
          date: info.last_modified_at,
          dateTime: info.modifiedDate,
        }}
        readTime={post.timeToRead || null}
        image={info.image}
        imageClass="background-element"
        contentClass="flex flex-col"
        headingClass="mt-auto w-full !pb-0"
        style={{
          minHeight: "48vh",
        }}
      />
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 mx-auto max-w-5xl gap-5">
        <div className="text-xs px-5 lg:px-0">
          <div className="lg:sticky top-28 pb-10 pr-5">
            {info.image && (
              <GatsbyImage
                image={getImage(info.image)}
                className="max-w-md mb-4 rounded"
              />
            )}

            {post.headings && post.headings.length ? (
              <>
                <h3 className="font-bold mb-2 text-sm">Table of Contents</h3>
                <ul>
                  {post.headings.map((heading) => {
                    return (
                      <li key={heading.id}>
                        <AnchorLink
                          to={`${post.fields.permalink}#${heading.id}`}
                          className={linkClass}
                        >
                          {heading.value}
                        </AnchorLink>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <></>
            )}

            <SeriesTOC post={post} series={series} />

            {/* <a href="https://www.paypal.com/donate/?hosted_button_id=ZMJ7T2NAX49GG" className="block bg-zinc-100 rounded mt-5 p-3 pr-4 max-w-xs hover:bg-felt-100" target="_blank" rel="noreferrer">
              <h4 className="font-bold mb-2 text-sm">Did this article help you?</h4>
              <p>Click to leave a tip to help keep this resource free!</p>
            </a> */}
            <a
              href="https://www.patreon.com/vegasaces"
              className="flex items-center bg-[#ff5900]/70 hover:bg-[#ff5900]/100 mt-5 py-3 px-4 max-w-xs rounded text-white"
            >
              <FontAwesomeIcon
                icon={faPatreon}
                className="text-white text-lg"
              />
              <span className="font-bold px-3 text-lg leading-snug">
                Support us on Patreon
              </span>
            </a>
          </div>
        </div>
        <div className="content col-span-1 lg:col-span-2 px-5 lg:px-0">
          {renderer(post.htmlAst)}

          <SeriesNavigation
            post={post}
            series={series}
            navHeaderText="Keep Reading"
          />

          {metadata && metadata.supplies && (
            <div className="max-w-2xl mx-auto pb-10">
              <Affiliate supplies={metadata.supplies} />
            </div>
          )}

          {info.tags?.includes("sponsored") && (
            <div className="mx-auto max-w-md text-center mb-20 bg-yellow-100 border border-yellow-400 p-2 text-sm rounded-lg">
              <p>
                <strong>Disclosure:</strong> This article contains sponsored
                content.
              </p>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
